import Image from 'next/image';

import { LoaderCircle } from 'lucide-react';

import { BrandLogo } from '@/components/layout/logo';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DotLoader } from '@/components/ui/loader';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/utils/cn';

interface LoadingPageProps {
  message?: string;
  className?: string;
  showAvatar?: boolean;
  variant?: 'fullPage' | 'section' | 'inline';
  avatarSrc?: string;
}

const defaultMessage = 'Ładowanie';

export const LoadingPage = ({
  className,
  message = defaultMessage,
  showAvatar = false,
  variant = 'fullPage',
  avatarSrc,
}: LoadingPageProps) => {
  const variants = {
    fullPage: 'h-[calc(100vh-4rem)] flex-col items-center justify-center space-y-6',
    inline: 'h-auto flex-row items-center justify-start space-x-3 py-2',
    section: 'h-[400px] flex-col items-center justify-center space-y-4',
  };

  const messageLayout = {
    fullPage: 'flex-col gap-2',
    inline: 'flex-row space-x-2',
    section: 'flex-col space-y-2',
  };

  return (
    <div aria-busy="true" aria-live="polite" className={cn('flex transition-all', variants[variant], className)}>
      <div className={cn('flex items-center', variant === 'inline' ? 'flex-row space-x-3' : 'flex-col space-y-4')}>
        {variant === 'fullPage' && <BrandLogo />}
        {showAvatar && (
          <div className="relative">
            {avatarSrc ? (
              <Avatar className="h-12 w-12 border-2 border-background">
                <AvatarFallback aria-label="User avatar" className="absolute inset-0" role="img" />
                <AvatarImage
                  className="absolute inset-0 h-full w-full object-cover data-error:invisible data-loading:invisible"
                  keepMounted
                  render={<Image alt="User avatar" height={48} src={avatarSrc} width={48} />}
                />
              </Avatar>
            ) : (
              <Skeleton className="h-12 w-12 rounded-full" />
            )}
            <div className="absolute right-0 bottom-0 h-3 w-3 animate-pulse rounded-full bg-primary" />
          </div>
        )}

        <div className={cn('flex items-center', messageLayout[variant])}>
          {variant !== 'fullPage' && <LoaderCircle aria-hidden="true" className="h-4 w-4 animate-spin" />}
          {variant === 'fullPage' && <DotLoader color="bg-muted-foreground" size="small" />}

          <p
            className={cn(
              'text-muted-foreground',
              variant !== 'fullPage' && 'animate-pulse',
              variant === 'fullPage' ? 'text-sm' : 'text-xs',
            )}
          >
            {message}
          </p>
        </div>
      </div>
    </div>
  );
};
